import {
  ApplicationManagement,
  GetApplicationManagementArgs,
  UpdateApplicationManagementArgs,
  UpdateApplicationSsrArgs,
  UpdateApplicationSsrResponse,
  UpdateNetworkAffiliateCodeArgs,
  UpdateNetworkAffiliateCodeErrorData,
  Feed,
  Policy,
  PartnerUserConfig,
  GetPartnerUserConfigArgs,
  SavePartnerUserConfigArgs,
} from './types';
import api from '../api/slice';

const applicationManagementApi = api.injectEndpoints({
  endpoints: builder => ({
    getApplicationManagement: builder.query<ApplicationManagement, GetApplicationManagementArgs>({
      query: ({ appId }) => `${appId}/application/management`,
      providesTags: (result, error, { appId }) => [{ type: 'ApplicationManagement', id: appId }],
    }),
    updateApplicationManagement: builder.mutation<{ rowsAffected: number }, UpdateApplicationManagementArgs>({
      query: ({ appId, applicationManagement, author }) => ({
        url: `${appId}/application/management`,
        method: 'PUT',
        body: {
          ...applicationManagement,
          Author: author,
        },
      }),
      invalidatesTags: (result, error, { appId }) => [{ type: 'ApplicationManagement', id: appId }],
    }),
    // SSR has its own endpoint: the general management PUT ignores EnableSSR/UserSplit.
    updateApplicationSsr: builder.mutation<UpdateApplicationSsrResponse, UpdateApplicationSsrArgs>({
      query: ({ appId, enableSsr, userSplit, author }) => ({
        url: `${appId}/application/management/ssr`,
        method: 'PUT',
        body: {
          EnableSSR: enableSsr,
          ...(userSplit !== undefined ? { UserSplit: userSplit } : {}),
          Author: author,
        },
      }),
      invalidatesTags: (result, error, { appId }) => [{ type: 'ApplicationManagement', id: appId }],
    }),
    updateNetworkAffiliateCode: builder.mutation<null, UpdateNetworkAffiliateCodeArgs>({
      query: ({ appId, networkId, affiliate_code }) => ({
        url: `${appId}/application/network-affiliate-code/${networkId}`,
        method: 'PUT',
        body: { affiliate_code },
        responseHandler: async response => {
          const text = await response.text();
          if (!text) return null;
          try {
            return JSON.parse(text) as UpdateNetworkAffiliateCodeErrorData;
          } catch {
            return null;
          }
        },
      }),
      invalidatesTags: (result, error, { appId }) => [{ type: 'ApplicationManagement', id: appId }],
    }),
    getFeeds: builder.query<Feed[], string>({
      query: appId => `${appId}/application/feed`,
      providesTags: ['feeds'],
      transformResponse: (response: Feed[]) => response.sort((a, b) => a.kind.localeCompare(b.kind)),
    }),
    updateFeeds: builder.mutation<void, { appId: string; feeds: Feed[] }>({
      query: ({ appId, feeds }) => ({
        url: `${appId}/application/feed`,
        method: 'PATCH',
        body: feeds,
      }),
      invalidatesTags: ['feeds'],
    }),
    getSplitPolicyOptions: builder.query<Policy[], void>({
      query: () => 'app-creation/policy',
    }),
    // An application has at most one partner_user row; none means no decision yet.
    getPartnerUserConfig: builder.query<PartnerUserConfig | null, GetPartnerUserConfigArgs>({
      query: ({ appId }) => ({ url: 'application-partner-user-config', params: { applicationID: appId } }),
      transformResponse: (response: PartnerUserConfig[]) => response?.[0] ?? null,
      providesTags: (result, error, { appId }) => [{ type: 'PartnerUserConfig', id: appId }],
    }),
    savePartnerUserConfig: builder.mutation<unknown, SavePartnerUserConfigArgs>({
      query: ({ appId, configId, partnerUserEnabled, reason, author }) =>
        configId
          ? {
              url: `application-partner-user-config/${configId}`,
              method: 'PUT',
              body: { PartnerUserEnabled: partnerUserEnabled, Reason: reason, ModifiedAuthor: author },
            }
          : {
              url: 'application-partner-user-config',
              method: 'POST',
              body: {
                ApplicationID: Number(appId),
                PartnerUserEnabled: partnerUserEnabled,
                Reason: reason,
                ModifiedAuthor: author,
              },
            },
      invalidatesTags: (result, error, { appId }) => [{ type: 'PartnerUserConfig', id: appId }],
    }),
  }),
});

export const {
  useGetApplicationManagementQuery,
  useUpdateApplicationManagementMutation,
  useUpdateApplicationSsrMutation,
  useUpdateNetworkAffiliateCodeMutation,
  useGetFeedsQuery,
  useUpdateFeedsMutation,
  useGetSplitPolicyOptionsQuery,
  useGetPartnerUserConfigQuery,
  useSavePartnerUserConfigMutation,
} = applicationManagementApi;
export default applicationManagementApi;
